import { useState } from 'react';
import { useStripeFinancials } from '../../context/StripeFinancialsContext';
import { Button, Icon, IconButton, Input, StatCard } from '../ui';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

interface EditableStatCardProps {
  label: string;
  value: string;
  hint?: React.ReactNode;
  editLabel: string;
  isLoading: boolean;
  initialValue: string;
  onSave: (amount: number) => Promise<void>;
}

/** StatCard with an in-place numeric editor (expenses / cash on hand). */
function EditableStatCard({ label, value, hint, editLabel, isLoading, initialValue, onSave }: EditableStatCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue);

  const handleSave = async () => {
    const numAmount = parseFloat(draft);
    if (isNaN(numAmount) || numAmount < 0) {
      alert('Please enter a valid positive number');
      return;
    }
    await onSave(numAmount);
    setIsEditing(false);
  };

  return (
    <div
      className="ck-statcard hover-lift"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        padding: 'var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <span className="t-eyebrow">{label}</span>
        {!isEditing && (
          <button
            onClick={() => {
              setDraft(initialValue);
              setIsEditing(true);
            }}
            // 36px tap target on phones (pulled into the card's padding), 24px on desktop
            className="pressable hover-wash w-9 h-9 -my-2.5 -mr-2.5 md:w-6 md:h-6 md:-my-1 md:-mr-1"
            aria-label={editLabel}
            title={editLabel}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'transparent',
              color: 'var(--text-faint)',
              cursor: 'pointer',
            }}
          >
            <Icon name="pencil" size={13} />
          </button>
        )}
      </div>

      {isEditing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <Input
            icon="circle-dollar-sign"
            fieldSize="sm"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button size="sm" onClick={handleSave} disabled={isLoading} style={{ flex: 1 }}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <IconButton icon="x" size="sm" variant="outline" label="Cancel" onClick={() => setIsEditing(false)} />
          </div>
        </div>
      ) : (
        <>
          <span
            className="t-num"
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              lineHeight: 'var(--leading-tight)',
              color: 'var(--text-body)',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
          {hint ? <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{hint}</span> : null}
        </>
      )}
    </div>
  );
}

export default function MetricsGrid() {
  const { metrics, isLoading, expenses, updateExpenses } = useStripeFinancials();

  const formatPercent = (value: number) => `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(1)}%`;

  const formatRunway = (months: number) => {
    if (months === 0 || !isFinite(months)) return 'N/A';
    if (months < 1) {
      const days = Math.round(months * 30);
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return `${months.toFixed(1)} mo`;
  };

  const netMTD = metrics.mtdRevenue - metrics.currentMonthExpenses;
  const margin = metrics.mtdRevenue > 0 ? Math.round((netMTD / metrics.mtdRevenue) * 100) : null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-[16px]">
      <StatCard
        label="Revenue MTD"
        value={formatCurrency(metrics.mtdRevenue)}
        delta={metrics.previousMonthRevenue > 0 ? formatPercent(metrics.growthRate) : undefined}
        hint={
          metrics.projectedRevenue > metrics.mtdRevenue
            ? `Projected: ${formatCurrency(metrics.projectedRevenue)}`
            : 'vs full last month'
        }
      />

      <EditableStatCard
        label="Expenses MTD"
        value={formatCurrency(metrics.currentMonthExpenses)}
        hint={
          expenses?.updated_at
            ? `Updated: ${new Date(expenses.updated_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}`
            : undefined
        }
        editLabel="Edit expenses"
        isLoading={isLoading}
        initialValue={expenses?.amount?.toString() || '0'}
        onSave={(amount) => updateExpenses(amount, expenses?.notes || '')}
      />

      <StatCard
        label="Net MTD"
        value={formatCurrency(netMTD)}
        delta={margin !== null ? `${netMTD >= 0 ? '+' : '-'}${Math.abs(margin)}%` : undefined}
        hint="margin"
      />

      <EditableStatCard
        label="Cash Runway"
        value={formatRunway(metrics.cashRunway)}
        hint={`Cash: $${expenses?.cash_on_hand?.toLocaleString() || '0'}`}
        editLabel="Edit cash on hand"
        isLoading={isLoading}
        initialValue={expenses?.cash_on_hand?.toString() || '0'}
        onSave={(cash) => updateExpenses(expenses?.amount || 0, expenses?.notes || '', cash)}
      />

      <StatCard label="Previous Month" value={formatCurrency(metrics.previousMonthRevenue)} hint="revenue" />

      <StatCard label="Revenue Run Rate" value={formatCurrency(metrics.runRate)} hint="MTD annualized" />

      <StatCard label="Profit Run Rate" value={formatCurrency(metrics.profitRunRate)} hint="YTD profit annualized" />
    </div>
  );
}
